import { useCallback, useEffect, useState } from 'react';
import { api, ErroApi } from '../lib/api';
import { Alerta, Botao, Cartao } from '../componentes/Interface';
import Layout from '../componentes/Layout';
import { useConfirmacao } from '../componentes/Confirmacao';

type Solicitacao = {
  id: number;
  nome: string;
  email: string;
  papelAtual: string;
  ministerio: string | null;
  ministerioChave: string | null;
  mensagem: string | null;
  resposta: string | null;
  status: string;
  decididoPor: string | null;
  criadoEm: string | null;
  decididoEm: string | null;
};

const quando = (iso: string | null) =>
  iso
    ? new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(iso))
    : '';

export default function Aprovacoes() {
  const [status, setStatus] = useState('pendente');
  const [lista, setLista] = useState<Solicitacao[]>([]);
  const [pendentes, setPendentes] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<number | null>(null);
  const { perguntar, dialogo } = useConfirmacao();

  const carregar = useCallback(async () => {
    try {
      const d = await api<{ solicitacoes: Solicitacao[]; pendentes: number }>(
        `/solicitacoes?status=${status}`,
      );
      setLista(d.solicitacoes);
      setPendentes(d.pendentes);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar os pedidos.');
    }
  }, [status]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function decidir(s: Solicitacao, decisao: 'aprovar' | 'recusar') {
    setOcupado(s.id);
    setErro(null);
    try {
      let resposta = '';
      if (decisao === 'recusar') {
        const r = await perguntar({
          titulo: `Recusar o pedido de ${s.nome}?`,
          texto: 'A pessoa continua com acesso de membro e recebe o motivo, se você escrever um.',
          confirmar: 'Recusar pedido',
          perigo: true,
          campo: { rotulo: 'Motivo (opcional)', sugestao: 'Ex.: vamos conversar antes' },
        });
        if (!r.ok) {
          setOcupado(null);
          return;
        }
        resposta = r.texto;
      }
      await api(`/solicitacoes/${s.id}/${decisao}`, {
        method: 'POST',
        body: JSON.stringify(resposta ? { resposta } : {}),
      });
      setAviso(
        decisao === 'aprovar'
          ? `${s.nome} agora faz parte do ${s.ministerio ?? 'ministerio'} e ja ve a escala.`
          : `Pedido de ${s.nome} recusado. Ele continua com acesso de membro.`,
      );
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui responder o pedido.');
    } finally {
      setOcupado(null);
    }
  }

  return (
    <Layout
      titulo="Aprovações"
      subtitulo="Quem se cadastrou dizendo que serve em algum ministério."
    >
      {erro && (
        <div className="mb-4 max-w-2xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}
      {aviso && (
        <div className="mb-4 max-w-2xl">
          <Alerta tipo="ok">{aviso}</Alerta>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {[
          { valor: 'pendente', rotulo: `Pendentes${pendentes ? ` (${pendentes})` : ''}` },
          { valor: 'aprovada', rotulo: 'Aprovados' },
          { valor: 'recusada', rotulo: 'Recusados' },
        ].map((f) => (
          <button
            key={f.valor}
            onClick={() => setStatus(f.valor)}
            className={`rounded-xl px-3.5 py-2 text-sm font-medium transition ${
              status === f.valor
                ? 'bg-tinta-900 text-white'
                : 'bg-white text-tinta-600 shadow-cartao hover:bg-tinta-100'
            }`}
          >
            {f.rotulo}
          </button>
        ))}
      </div>

      {lista.length === 0 ? (
        <Cartao>
          <p className="text-sm text-tinta-400">
            {status === 'pendente' ? 'Nenhum pedido esperando resposta.' : 'Nada por aqui.'}
          </p>
        </Cartao>
      ) : (
        <div className="grid max-w-4xl gap-3">
          {lista.map((s) => (
            <Cartao key={s.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-tinta-900">{s.nome}</p>
                  <p className="text-sm text-tinta-400">{s.email}</p>
                  <p className="mt-1 text-sm text-tinta-700">
                    Pediu para servir em <b>{s.ministerio ?? 'ministério não informado'}</b>
                  </p>
                  {s.mensagem && (
                    <p className="mt-1 rounded-xl bg-tinta-100 px-3 py-2 text-sm text-tinta-600">
                      "{s.mensagem}"
                    </p>
                  )}
                  <p className="mt-1 text-xs text-tinta-400">
                    pedido em {quando(s.criadoEm)}
                    {s.decididoEm ? ` · respondido por ${s.decididoPor} em ${quando(s.decididoEm)}` : ''}
                  </p>
                  {s.resposta && (
                    <p className="mt-1 text-xs text-tinta-400">Resposta: {s.resposta}</p>
                  )}
                </div>

                {s.status === 'pendente' ? (
                  <div className="flex shrink-0 gap-2">
                    <Botao
                      variante="secundario"
                      disabled={ocupado === s.id}
                      onClick={() => void decidir(s, 'recusar')}
                    >
                      Recusar
                    </Botao>
                    <Botao carregando={ocupado === s.id} onClick={() => void decidir(s, 'aprovar')}>
                      Aprovar
                    </Botao>
                  </div>
                ) : (
                  <span
                    className={`shrink-0 rounded-lg px-2.5 py-1 text-xs font-semibold ${
                      s.status === 'aprovada' ? 'bg-tinta-900 text-white' : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {s.status === 'aprovada' ? 'aprovado' : 'recusado'}
                  </span>
                )}
              </div>
            </Cartao>
          ))}
        </div>
      )}
      {dialogo}
    </Layout>
  );
}
